import {Injectable} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {of, Subject, Observable} from 'rxjs';
import {catchError, map, tap} from 'rxjs/internal/operators';
import {Action, ActionSummary} from '../model/action';
import {ActionDetails} from '../../actions/model/action-details';
import {PaginationResponse} from '../model/paginationResponse';
import {ApiRoutes} from '../../../config/api-routes';
import {AddActionsRequest} from '../model/add-actions-request';
import {ActionLogSummary} from '../../actions/model/action-log-summary';
import { Issue } from '../model/issue';
import { Multimedium } from '../model/multimedium';
import { UpdateActionRequest } from '../model/updateActionRequest';
import {ActionStatusType} from '../enums/enums';
import {InterventionTeam} from '../model/intervention-team';

@Injectable()
export class ActionsService {
  detailsUpdated$ = new Subject<any>();

  constructor(private httpClient: HttpClient, private translate: TranslateService, private config: ApiRoutes) {}

  public getLatestActions(): Observable<ActionSummary[]> {
    return this.getActions(new HttpParams(), 1, 5)
      .pipe(
        map(actions  => actions.items)
      );
  }

  public getActions(httpParams: HttpParams, pageNumber: number, pageSize: number): Observable<PaginationResponse<ActionSummary>> {
    const params = httpParams
      .append('pageNumber', pageNumber?.toString())
      .append('pageSize', pageSize?.toString());
    return this.httpClient.get<PaginationResponse<ActionSummary>>(this.config.ACTIONS, {params}).pipe(
      map(removeActionsWithIssues),
      catchError(() => of({items: [], page: 0, total: 0} as PaginationResponse<ActionSummary>))
    );
  }

  public getDetails(actionId: string): Observable<ActionDetails> {
    return this.httpClient.get<ActionDetails>(ApiRoutes.getRouteWithInjectedParams(this.config.ACTION_DETAILS, [actionId])).pipe(
      catchError(() => of({} as ActionDetails))
    );
  }

  public updateDetails(action: ActionDetails, comment?: string): Observable<boolean> {
    const request: UpdateActionRequest = { action, comment };
    return this.httpClient.put<boolean>(this.config.ACTIONS, request).pipe(
      tap(() => this.detailsUpdated$.next(null)),
      catchError(() => of(true))
    );
  }

  public getActionMedia(actionId: string): Observable<Multimedium[]> {
    return this.httpClient.get<Multimedium[]>(ApiRoutes.getRouteWithInjectedParams(this.config.ACTION_MEDIA, [actionId])).pipe(
      catchError(() => of([]))
    );
  }

  public getActionLogs(actionId: string): Observable<ActionLogSummary[]> {
    return this.httpClient.get<ActionLogSummary[]>(ApiRoutes.getRouteWithInjectedParams(this.config.ACTION_LOGS, [actionId])).pipe(
      catchError(() => of([]))
    );
  }

  public getActionIssues(actionId: string): Observable<PaginationResponse<Issue>> {
    return this.httpClient.post<PaginationResponse<Issue>>(this.config.ISSUES, { actionId }).pipe(
      catchError(() => of({} as PaginationResponse<Issue>))
    );
  }

  public addAction(issueGuids: string[], action: ActionDetails): Observable<string> {
    const addActionsRequest = new AddActionsRequest(action.type, action.status, action.urgency,
      action.instructions, action.dueDate, action.interventionTeamId, issueGuids, action.priority,
      action.reference, action.workOrderReference);
    return this.httpClient.post<string>(this.config.ACTIONS, addActionsRequest).pipe(
      tap(() => this.detailsUpdated$.next(null)),
      catchError(() => of(null as string))
    );
  }

  public getIssueActions(issueGuid: string): Observable<PaginationResponse<ActionSummary>> {
    return this.httpClient.get<PaginationResponse<ActionSummary>>(
      ApiRoutes.getRouteWithInjectedParams(this.config.ISSUE_ACTIONS, [issueGuid])
    );
  }

  public sendNotification(actionId: string) {
    const route = ApiRoutes.getRouteWithInjectedParams(this.config.ACTION_SEND_NOTIFICATION, [actionId]);
    return this.httpClient.post<ActionLogSummary[]>(route, {})
      .pipe(catchError(() => of([])));
  }


  public completeActions(actionIds: string[], status: ActionStatusType): Observable<string[]> {
    return this.httpClient.post<string[]>(
      this.config.ACTIONS_COMPLETE,
      {
        actionIds: actionIds,
        status: status
      }
    );
  }

  public updateWorkOrderReference(actionId: string): Observable<number> {
    const route = ApiRoutes.getRouteWithInjectedParams(this.config.ACTION_UPDATE_WORKORDERREFERENCE, [actionId]);
    return this.httpClient.post<number>(route, {})
      .pipe(catchError(() => of(null)));
  }

  /**
   * Gets the Actions that a given team is currently
   * working on or has pending to accept.
   */
  public getActiveActionForTeam(team: InterventionTeam, page?: number, pageSize?: number) {
    const params = (new HttpParams())
      .append('InterventionTeams', team.id)
      .append('Statuses', ActionStatusType.SENT)
      .append('Statuses', ActionStatusType.ACCEPTED)
      .append('Statuses', ActionStatusType.EXECUTING);

    return this.getActions(params, page, pageSize);
  }

  public unassignAction(action: Action): Observable<void> {
    const route = ApiRoutes.getRouteWithInjectedParams(this.config.ACTION_UNASSIGN, [action.id]);
    return this.httpClient.delete<void>(route);
  }
}

/**
 * Filters out Actions that might not have an Issue
 * in their result set. Temporary fix until
 * MAIN-60 is resolved.
 */
function removeActionsWithIssues(response: PaginationResponse<ActionSummary>): PaginationResponse<ActionSummary> {
  return {
    ...response,
    items: response.items.filter(({issues}) => issues?.length > 0)
  };
}
